import { useContext } from "react";
import { useHistory } from "react-router-dom";
import { Panel, ButtonBar } from "@k8slens/lds";

import { useAnalytics } from "src/hooks/useAnalytics";
import { ProfileContext } from "src/providers/profile-provider";
import { useAcceptBusinessInvitation } from "src/hooks/useAcceptBusinessInvitation";
import { useBusinessInvitation } from "src/hooks/useBusinessInvitation";

import Button from "src/components/Button/TrackedButton";
import LinkButton from "src/components/Button/TrackedLinkButton";
import PublicLayout from "src/components/PublicLayout/PublicLayout";
import CurrentUser from "src/components/CurrentUser/CurrentUser";
import Notification from "src/components/Notification/Notification";
import TrackedAnchor from "src/components/TrackedAnchor/TrackedAnchor";

import styles from "./AcceptBusinessInvitation.module.css";

const AcceptBusinessInvitation: React.FC = () => {
  const { track, trackError } = useAnalytics();
  const history = useHistory();
  const { acceptBusinessInvitation, invitationError } = useAcceptBusinessInvitation();
  const { profile } = useContext(ProfileContext);
  const { invitation, error } = useBusinessInvitation();
  const username = profile?.username;

  const handleAccept = async () => {
    if (invitation?.businessId && invitation?.invitationId) {
      const businessInvitation = await acceptBusinessInvitation(invitation.businessId, invitation.invitationId);

      if (businessInvitation) {
        track("Business Invitation Accepted");
        history.push(`/business/${invitation.businessId}`, {
          invitationId: invitation.invitationId,
        });

        return true;
      }
      trackError("Business Invitation Accepting Failed", { invitationId: invitation.invitationId });

      return true;
    }

    return false;
  };

  const invalidKey = Boolean(error) || !invitation;

  return (
    <PublicLayout size="xl" className={styles.acceptInvitation} footer={<CurrentUser />}>
      <Panel
        className={styles.panel}
        header={
          <>
            <h2>Accept Business ID Invitation</h2>
          </>
        }
        footer={
          <ButtonBar type="grid" gridSize={4}>
            <LinkButton to="/home" label="Cancel" discreet />
            <div />
            <div />
            <Button label="Accept" onClick={handleAccept} loadingStateOnPromise disabled={!username} primary />
          </ButtonBar>
        }
      >
        <>
          {!invalidKey && (
            <p>
              This invitation will assign the Lens ID <strong>{username}</strong> to be an admin of the Lens Business ID{" "}
              <strong>{invitation?.company}</strong>.
            </p>
          )}
          {invitationError && <Notification level="error" message={invitationError} type="flash" />}
          {invalidKey && (
            <div className={styles.error}>
              <h4>Something went wrong</h4>
              <p>There seems to be some issues with your invitation.</p>
              <p>
                Try re-opening the link in your invitation email. If that doesn&apos;t help, please contact us at{" "}
                <TrackedAnchor href="mailto:info@k8slens.dev">info@k8slens.dev.</TrackedAnchor>
              </p>
            </div>
          )}
        </>
      </Panel>
    </PublicLayout>
  );
};

export default AcceptBusinessInvitation;
